import { LightningElement, api, wire } from "lwc";
import { localizeNumber } from "@devforce/trailhead-client-locale";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { BooleanAttr, attrToBool, flattenOptions } from "common/helpers";

import {
  FormControl,
  FormControlValue,
  FormControlChangeEvent,
} from "../formControl/types";
import { FilterSort, FilterPill } from "./types";
import * as locales from "./locales";

// Changes to these values must be reflect in the stylesheet
export const BREAKPOINT = 48 * 16; // 48rem
export const DEFAULT_CONTROL_WIDTH = 8 + 180 + 8; // width + gutters

export function removeValue(
  filter: FormControl,
  value?: FormControlValue
): FormControlValue {
  if (
    value == null ||
    !filter.multiple ||
    !Array.isArray(filter.value) ||
    Array.isArray(value)
  ) {
    return null;
  }

  // Remove item from array
  const newValue = filter.value.filter((v: FormControlValue) => v !== value);

  // Return null if the array is empty
  return newValue.length ? newValue : null;
}

export default class extends LightningElement {
  @api filters: Array<FormControl> | null = null;
  @api sort: FilterSort | null = null;
  @api resultCount: string | null = null;
  @api busy: BooleanAttr = false;

  private isDrawerClosed: boolean = true;
  private threshold: number = 1;
  private isFilterMode: boolean = false;
  private isSortMode: boolean = false;

  private get isBusy() {
    return attrToBool(this.busy);
  }

  private get visibleFilters() {
    return this.filters
      ? this.filters.filter((f, i) => i < this.threshold)
      : [];
  }

  private get drawerFilters() {
    return this.filters
      ? this.filters.map((f) => {
          if (f.type === "combobox") {
            return {
              ...f,
              variant: "accordion",
            };
          }

          return f;
        })
      : [];
  }

  private get hiddenFilters() {
    return this.filters
      ? this.filters.filter((f, i) => i >= this.threshold)
      : [];
  }

  private get formattedResultCount() {
    return this.resultCount ? localizeNumber(this.resultCount) : 0;
  }

  private get sortConfig() {
    return {
      toggleDisabled: true,
      ...this.sort,
    };
  }

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private _labels: LabelsAdapterValue = null!;
  private get labels() {
    return {
      ...this._labels,
      drawer_button:
        this.resultCount != null
          ? this._labels.drawer_button_with_count
          : this._labels.drawer_button,
      drawer_heading: this.isSortMode
        ? this._labels.drawer_heading_sort
        : this._labels.drawer_heading_filter,
      drawer_subHeading:
        this.resultCount != null
          ? this._labels.drawer_subheading_with_count
          : null,
      sort_value: this.sortValueLabel,
    };
  }

  private get labelOptions() {
    return {
      drawer_button_with_count: {
        count: this.formattedResultCount,
      },
      drawer_subheading_with_count: {
        count: this.formattedResultCount,
      },
      filter_value: {
        count: this.pills ? this.pills.length : 0,
      },
    };
  }

  connectedCallback() {
    window.addEventListener("resize", this.handleWindowResize);
  }

  renderedCallback() {
    this.queueDetectOverflow();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleWindowResize);
  }

  // Width to use as divisor when determining how many controls overflow into drawer
  private get baseControlWidth() {
    const el = this.template.querySelector(
      // Use last control because it has extra margin
      ".controls__container .form-control:last-of-type"
    );

    if (!el) {
      return DEFAULT_CONTROL_WIDTH;
    }

    const { minWidth, marginRight, marginLeft } = getComputedStyle(el);

    return parseInt(minWidth) + parseInt(marginRight) + parseInt(marginLeft);
  }

  private get hasDrawer() {
    return !!(this.hiddenFilters && this.hiddenFilters.length);
  }

  private get isDrawerButtonHidden() {
    return !this.hasDrawer;
  }

  private get sortValueLabel() {
    if (!this.sort) {
      return "";
    }

    const option = this.sort.options.find((o) => o.value === this.sort!.value);

    return option ? option.label : "";
  }

  // Convert filter values into individual pills
  private get pills() {
    if (!this.filters) return null;

    const pills = this.filters.reduce((arr, f) => {
      if (f.value) {
        // Handle multiple values
        const values = ([] as string[]).concat(f.value);

        return arr.concat(
          values.map((v): FilterPill => {
            // Use the option label if it exists either as a top level option or in a group
            const flatOptions = flattenOptions(f.options || []);
            const option = flatOptions.find((o) => o.value === v);

            return {
              ...f,
              key: `${f.label}_${v}`,
              value: v,
              label: (option && option.label) || v,
              filterLabel: f.label,
              onRemove: () => {
                this.handleRemoveValue(f.id, v);
              },
            };
          })
        );
      }

      return arr;
    }, [] as FilterPill[]);

    return pills.length ? pills : null;
  }

  private queueDetectOverflow() {
    requestAnimationFrame(this.detectOverflow.bind(this));
  }

  // Put overflowing filters into the drawer
  private detectOverflow() {
    // Return early if screen is small
    if (window.innerWidth <= BREAKPOINT) {
      this.threshold = 0;
      return;
    }

    try {
      // container width - sort width - button width
      const btnStyles = getComputedStyle(
        this.template.querySelector(".controls__drawer-button")!
      );

      const availableWidth =
        this.template.host.offsetWidth -
        this.template.querySelector(".form-control:not(.form-control--sort)")!
          .offsetLeft -
        parseInt(btnStyles.width) +
        parseInt(btnStyles.marginLeft);

      // Determine how many controls to show, but always show at least one
      this.threshold = Math.max(
        1,
        Math.floor(availableWidth / this.baseControlWidth)
      );
    } catch (error) {
      this.threshold = 1;
    }
  }

  private handleFilter(e: FormControlChangeEvent) {
    this.dispatchEvent(new CustomEvent("filter", e));
  }

  private handleSort(e: FormControlChangeEvent) {
    this.dispatchEvent(new CustomEvent("sort", e));
  }

  private handleRemoveValue(id: string, value: FormControlValue) {
    const filter = this.filters
      ? this.filters.find((f) => f.id === id)
      : undefined;
    const newValue = filter ? removeValue(filter, value) : null;
    const event = <FormControlChangeEvent>{
      detail: {
        id,
        value: newValue,
      },
    };

    this.dispatchEvent(new CustomEvent("filter", event));
  }

  private handleReset() {
    this.dispatchEvent(new CustomEvent("reset"));
  }

  private handleWindowResize = () => {
    this.queueDetectOverflow();

    if (!this.hasDrawer) {
      this.isDrawerClosed = true;
    }
  };

  private openDrawer() {
    this.isDrawerClosed = false;
    document.body.style.overflow = "hidden";
  }

  private openFilterDrawer() {
    this.isFilterMode = true;
    this.openDrawer();
  }

  private openSortDrawer() {
    this.isSortMode = true;
    this.openDrawer();
  }

  private closeDrawer() {
    this.isDrawerClosed = true;
    document.body.style.overflow = "";
  }

  private resetDrawer() {
    if (this.isDrawerClosed) {
      this.isFilterMode = false;
      this.isSortMode = false;
    }
  }
}
